


import { Component, Vue, Watch } from "vue-property-decorator";

import { Users } from "App";
import { EventBus } from "App/IOC";
import { Dictionary } from "App/Contracts";
import { CustomNotification, User } from "App/Models";

import { GetTheme, ToggleTheme, ThemeMode } from "Web/Theme";

import Popup from "Web/Components/Common/Popup.vue";
import Modal from "Web/Components/Common/Modal.vue";
import UnderConstruction from "Web/Views/UnderConstruction.vue";

const COLLAPSE_KEY = "prospeneo-sidebar-collapsed";

@Component({
    components: {
        Modal,
        Popup,
        UnderConstruction
    }
})
export default class Menu extends Vue {
    $refs!: Vue[ "$refs" ] & {
        SignoutPopup: Popup
    }

    private quickCreationForm: string = "";
    private quickCreateTitle: string = "";
    private quickCreateSuccess: string = "";

    private user? = {} as User;

    private lockActions: boolean = false;

    private collapsed: boolean = false;
    private theme: ThemeMode = "light";

    /* État de repli mémorisé avant l'entrée dans Paramètres : la section Paramètres
       a sa propre sous-navigation, donc on minimise le menu principal par défaut le
       temps d'y rester, puis on restaure l'état choisi par l'utilisateur en sortant.
       null = on n'est pas dans Paramètres. */
    private collapsedBeforeSettings: boolean | null = null;

    /* Groupes dépliables ouverts (clé = identifiant du groupe). */
    private openGroups: Dictionary<boolean> = {};

    /* Chemins regroupés par menu dépliable. */
    private readonly productPaths: string[] = [
        "/products", "/coupons", "/price-targets", "/configuration", "/yearly-coupon"
    ];
    private readonly configPaths: string[] = [ "/settings" ];

    /* Portails d'annonces : groupe dépliable. Le 1er sous-menu (configuration générale) porte la
       liste actuelle ; le 2nd ouvre les paramétrages par point de vente. */
    private readonly adsSalePointPath: string = "/ads-publications/sale-point-settings";
    private readonly adsPaths: string[] = [ "/ads-publications" ];

    private get productsActive(): boolean {
        return this.isAnyActive( this.productPaths );
    }

    private get productsOpen(): boolean {
        return this.isGroupOpen( "products", this.productPaths );
    }

    private get configActive(): boolean {
        return this.isAnyActive( this.configPaths );
    }

    private get adsActive(): boolean {
        return this.isAnyActive( this.adsPaths );
    }

    private get adsOpen(): boolean {
        return this.isGroupOpen( "ads", this.adsPaths );
    }

    /* General config sub-item = the list view and any single portal edit page, but not the
       sale-point settings sub-page. */
    private get adsGeneralActive(): boolean {
        return this.isAnyActive( this.adsPaths )
            && !this.$route.path.startsWith( this.adsSalePointPath );
    }

    /* Reponses IA : groupe depliable ( Fred, 26/09/2026 ). La vue generale est
       `/agent-ia` EXACTEMENT ; « Par utilisateur » vit sous `/agent-ia/users`. */
    private readonly agentPaths: string[] = [ "/agent-ia" ];
    private readonly agentUsersPath: string = "/agent-ia/users";
    private readonly agentConversationsPath: string = "/agent-ia/conversations";
    private readonly agentUsagePath: string = "/agent-ia/usage";

    private get agentActive(): boolean {
        return this.isAnyActive( this.agentPaths );
    }

    private get agentOpen(): boolean {
        return this.isGroupOpen( "agent", this.agentPaths );
    }

    private get agentUsersActive(): boolean {
        return this.$route.path.startsWith( this.agentUsersPath );
    }

    private get agentConversationsActive(): boolean {
        return this.$route.path.startsWith( this.agentConversationsPath );
    }

    private get agentUsageActive(): boolean {
        return this.$route.path.startsWith( this.agentUsagePath );
    }

    private get agentGeneralActive(): boolean {
        return this.$route.path === "/agent-ia";
    }

    /* API : groupe a part ( Fred, 06/10/2026 : « un menu specifique pour l'API » ).
       Une seule entree aujourd'hui, les cles des clients ; le groupe accueillera
       ce qui suivra ( serveur MCP, journal des appels ). */
    private readonly apiPaths: string[] = [ "/api-keys" ];

    private get apiActive(): boolean {
        return this.isAnyActive( this.apiPaths );
    }

    private get apiOpen(): boolean {
        return this.isGroupOpen( "api", this.apiPaths );
    }

    private get adsSalePointActive(): boolean {
        return this.$route.path.startsWith( this.adsSalePointPath );
    }

    /* Logo de la charte 2026 ( Fred, 02/10/2026 : « le nouveau logo de l'app » ), les memes
       fichiers que le desktop ( `src/Web/Assets/SVG/prospeneo-logo-*.svg` ) : mot-marque fonce
       en clair, blanc en sombre. Replie, seule la goutte se montre ( meme dessin, rogne ). */
    private get logoLarge(): string {
        return this.theme === "dark"
            ? require( "../../../Assets/Images/prospeneo-logo-dark.svg" )
            : require( "../../../Assets/Images/prospeneo-logo-light.svg" );
    }

    private get logoSmall(): string {
        return this.logoLarge;
    }

    async created() {
        this.collapsed = localStorage.getItem( COLLAPSE_KEY ) === "1";
        this.theme = GetTheme();

        /* Prend en compte un chargement direct sur /settings. */
        this.syncSettingsCollapse( this.$route.path );

        /* Garde le placeholder {} si la session courante n'est pas résolue (ex.
           /Users/Current renvoie 401) : sinon user devient undefined et le rendu
           du profil sidebar plante sur user.Initials. */
        const current = await Users.GetCurrent();
        if( current !== undefined )
            this.user = current;

        EventBus.Subscribe( "LockMenuActions", this.toggleMenuActionsLock );
    }

    private toggleMenuActionsLock( value: boolean ): void {
        this.lockActions = value;
    }

    private toggleCollapse(): void {
        this.collapsed = !this.collapsed;
        localStorage.setItem( COLLAPSE_KEY, this.collapsed ? "1" : "0" );

        /* Repli manuel dans Paramètres : cet état devient la nouvelle référence à
           restaurer en sortant, sinon on écraserait le choix de l'utilisateur. */
        if( this.collapsedBeforeSettings !== null )
            this.collapsedBeforeSettings = this.collapsed;
    }

    @Watch( "$route.path" )
    private onRouteChange( path: string ): void {
        this.syncSettingsCollapse( path );
    }

    /* Minimise le menu principal par défaut tant qu'on est dans Paramètres (sous-nav
       dédiée), et restaure l'état précédent en sortant. Ne touche pas la préférence
       persistée (localStorage) : c'est un repli contextuel, pas un choix global. */
    private syncSettingsCollapse( path: string ): void {
        const inSettings = path.startsWith( "/settings" );

        if( inSettings ) {
            if( this.collapsedBeforeSettings === null ) {
                this.collapsedBeforeSettings = this.collapsed;
                this.collapsed = true;
            }
            return;
        }

        if( this.collapsedBeforeSettings !== null ) {
            this.collapsed = this.collapsedBeforeSettings;
            this.collapsedBeforeSettings = null;
        }
    }

    private toggleTheme(): void {
        this.theme = ToggleTheme();
    }

    private toggleGroup( id: string ): void {
        this.$set( this.openGroups, id, !this.openGroups[ id ] );
    }

    /* Support clients, Portails d'annonces et Produits ( Fred, 02/10/2026 ) : deplier le groupe
       depuis un autre ecran ouvre aussi son premier sous-menu, actif par defaut ;
       depuis l'un de ses ecrans, le clic replie ou deplie seulement. */
    private openGroup( id: string, active: boolean, path: string ): void {
        if( active ) {
            this.toggleGroup( id );

            return;
        }

        this.$set( this.openGroups, id, true );
        void this.navigate( path );
    }

    /* Un groupe est ouvert s'il a été déplié ou s'il contient la route active. */
    private isGroupOpen( id: string, paths: string[] ): boolean {
        if( this.openGroups[ id ] !== undefined )
            return this.openGroups[ id ];

        return this.isAnyActive( paths );
    }

    private isActive( path: string ): boolean {
        return this.$route.path === path;
    }

    private isAnyActive( paths: string[] ): boolean {
        return paths.some( p => this.$route.path.startsWith( p ) );
    }

    private async signOut(): Promise<void> {
        this.$refs.SignoutPopup.Hide();
        await Users.SignOut();
        this.$router.replace( "/user/signin" );
    }

    private async navigate( path: string, query?: Dictionary<any> ) {
        /* Catch navigation error and ignore "NavigationDuplicated". */
        this.$router.push( { path, query } )
            .catch( error => {
                if( error.name !== "NavigationDuplicated" )
                    console.error( error );
            });
    }

    private quickCreate( type: string ) {
        this.quickCreationForm = type + "Form";

        this.quickCreateTitle = this.$t( "GLOBAL_quick" + type + "Title" ).toString();
        this.quickCreateSuccess = this.$t( "GLOBAL_quick" + type + "Created" ).toString();
    }

    private notifyCreation() {
        EventBus.Publish(
            "Notification",
            CustomNotification.Success( this.quickCreateTitle, this.quickCreateSuccess )
        );
    }

    private openHelpWindow() {
        window.open( "https://aide.prospeneo.com" );
    }

    /* "Nouveautés" : lien fourni ultérieurement par Fred. Placeholder en attendant. */
    private openWhatsNew(): void {
        EventBus.Publish( "UnderConstruction" );
    }

    private underConstruction( event: MouseEvent ): void {
        event.preventDefault();

        EventBus.Publish( "UnderConstruction" );
    }
}
